import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { UnitInput } from "@/components/primitives/unit-input";
import { UseFormReturn } from "react-hook-form";
import { CreateIngredientFormData } from "./schema";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { useCallback } from "react";
import { ExternalLink } from "@/components/primitives/external-link";
import { IngredientCategory } from "@/lib/types";

type AdvancedFormProps = {
  form: UseFormReturn<CreateIngredientFormData>;
  isDisabled?: boolean;
  selectedCategory: IngredientCategory;
};

type AdvancedSectionProps = {
  form: UseFormReturn<CreateIngredientFormData>;
  isDisabled?: boolean;
  selectedCategory: IngredientCategory;
};

export function AdvancedCompositionSection({
  form,
  isDisabled,
  selectedCategory,
}: AdvancedSectionProps) {
  const triggerCompositionValidation = useCallback(() => {
    form.trigger([
      "waterPct",
      "fatPct",
      "sugarsPct",
      "alcoholPct",
      "composition",
    ]);
  }, [form]);

  return (
    <>
      <FormField
        control={form.control}
        name="waterPct"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Water %</FormLabel>
            <FormControl>
              <UnitInput
                {...field}
                unit="%"
                disabled={isDisabled}
                onChange={(e) => {
                  field.onChange(e);
                  triggerCompositionValidation();
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="fatPct"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Fat %</FormLabel>
            <FormControl>
              <UnitInput
                {...field}
                unit="%"
                disabled={isDisabled}
                onChange={(e) => {
                  field.onChange(e);
                  triggerCompositionValidation();
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="sugarsPct"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Sugars %</FormLabel>
            <FormControl>
              <UnitInput
                {...field}
                unit="%"
                disabled={isDisabled}
                onChange={(e) => {
                  field.onChange(e);
                  triggerCompositionValidation();
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {selectedCategory === "dairy" && (
        <FormField
          control={form.control}
          name="lactosePct"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Lactose %</FormLabel>
              <FormControl>
                <UnitInput {...field} unit="%" disabled={isDisabled} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={form.control}
        name="alcoholPct"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Alcohol %</FormLabel>
            <FormControl>
              <UnitInput
                {...field}
                unit="%"
                disabled={isDisabled}
                onChange={(e) => {
                  field.onChange(e);
                  triggerCompositionValidation();
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {selectedCategory === "chocolate" && (
        <FormField
          control={form.control}
          name="cocoaFatPct"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Cocoa Butter %</FormLabel>
              <FormControl>
                <UnitInput {...field} unit="%" disabled={isDisabled} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {selectedCategory === "chocolate" && (
        <FormField
          control={form.control}
          name="cocoaSolidsNonfatPct"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Cocoa Solids %</FormLabel>
              <FormControl>
                <UnitInput {...field} unit="%" disabled={isDisabled} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {(selectedCategory === "emulsifier" || selectedCategory === "egg") && (
        <FormField
          control={form.control}
          name="emulsifierPct"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Emulsifier %</FormLabel>
              <FormControl>
                <UnitInput {...field} unit="%" disabled={isDisabled} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      <FormField
        control={form.control}
        name="stabilizerPct"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Stabilizer %</FormLabel>
            <FormControl>
              <UnitInput {...field} unit="%" disabled={isDisabled} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="pod"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Relative Sweetness</FormLabel>
            <FormControl>
              <UnitInput {...field} unit="%" disabled={isDisabled} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="pac"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              FPDF <ExternalLink href="/learn/fpdf">Learn more</ExternalLink>
            </FormLabel>
            <FormControl>
              <UnitInput {...field} unit="%" disabled={isDisabled} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {form.formState.errors.composition?.message && (
        <Alert variant="destructive" className="col-span-2">
          <AlertTitle>{form.formState.errors.composition.message}</AlertTitle>
        </Alert>
      )}
    </>
  );
}

export function AdvancedNutritionSection({
  form,
  isDisabled,
}: AdvancedSectionProps) {
  return (
    <>
      <div className="col-span-2 mt-4 text-sm font-medium">
        Nutritional Info (optional)
      </div>
      <FormField
        control={form.control}
        name="energyKcal"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Calories (kcal/g)</FormLabel>
            <FormControl>
              <UnitInput {...field} unit="kcal/g" disabled={isDisabled} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="saturatedFatPct"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Saturated Fat %</FormLabel>
            <FormControl>
              <UnitInput {...field} unit="%" disabled={isDisabled} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="transFatPct"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Trans Fat %</FormLabel>
            <FormControl>
              <UnitInput {...field} unit="%" disabled={isDisabled} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="carbPct"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Carbs %</FormLabel>
            <FormControl>
              <UnitInput {...field} unit="%" disabled={isDisabled} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="fiberPct"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Fiber %</FormLabel>
            <FormControl>
              <UnitInput {...field} unit="%" disabled={isDisabled} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="proteinPct"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Protein %</FormLabel>
            <FormControl>
              <UnitInput {...field} unit="%" disabled={isDisabled} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}

export function AdvancedForm({
  form,
  isDisabled,
  selectedCategory,
}: AdvancedFormProps) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <AdvancedCompositionSection
        form={form}
        isDisabled={isDisabled}
        selectedCategory={selectedCategory}
      />
      <AdvancedNutritionSection
        form={form}
        isDisabled={isDisabled}
        selectedCategory={selectedCategory}
      />
    </div>
  );
}
